import { paymentServiceLinkId, settleServiceLinkId, tokenId } from "@/assets/googleTrackIds";
import { CheckCircleFilled, CopyFilled } from "@payping/icons";
import { createClientToken } from "@payping/redux/actions";
import { dateFormats, utcNow } from "@payping/utils/dateUtils";
import { Trans } from "@payping/utils/localeUtils";
import {
  Badge,
  Button,
  Input,
  Layout,
  LegacyForm,
  Modal,
  Space,
  Typography,
  message,
  useMessagedAsyncFn,
} from "@payping/web-components";
import classNames from "classnames";
import copy from "copy-to-clipboard";
import { useState } from "react";

import { buildUrl } from "@payping/utilities-shared/url";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "../../../assets/scss/Settlement/Settlement.module.scss";
import { URLS } from "@payping/utilities-shared/consts";

const WebServices = () => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [tokenResult, setTokenResult] = useState("");
  const dispatch = useAppDispatch();
  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values) => {
      const tokenBody = {
        clientName: "Settlement-Auto-Created-Token",
        clientUri: URLS.BASE_URL,
        description: "This Token is Auto Generated for Settlement web services",
        expireDate: utcNow({
          manipulate: [{ args: [1, "year"], method: "add" }],
          toFormat: dateFormats.standard.utcDateTime,
        }).string,
        isTest: false,
        password: values.password,
        scopes: ["settlement:read", "settlement:write"],
      };
      const { token } = await dispatch(createClientToken(tokenBody));
      setTokenResult(token || "");
    },
    [dispatch],
    undefined,
    { useProgress: true },
  );

  return (
    <Space orientation={"vertical"}>
      <Typography.Title level={4}>{t("settlement.requiredWebServices.title")}</Typography.Title>
      <Typography.Text>{t("settlement.requiredWebServices.desc")}</Typography.Text>
      <Space orientation={"vertical"} size={"large"}>
        <Space size={"middle"}>
          <a
            href={buildUrl(URLS.GIT_PAGES_URL, "cash-out").toString()}
            target={"_blank"}
            rel="noreferrer"
            data-google-track={settleServiceLinkId}
          >
            {t("settlement.webserviceHelp")}
          </a>
          <Badge dot={true}>
            <a
              target={"_blank"}
              href={"https://zify.ir/dashboard/settings/token/"}
              rel="noreferrer"
              data-google-track={paymentServiceLinkId}
            >
              <Space>{t("settlement.requiredWebServices.help")}</Space>
            </a>
          </Badge>
        </Space>
        <Typography.Title level={4}>{t("settlement.tokenSettlement.title")}</Typography.Title>
        <Typography.Text>{t("settlement.tokenSettlement.desc")}</Typography.Text>
        <Button
          loading={loadingSubmit}
          onClick={() => {
            setModalVisible(true);
          }}
          data-google-track={tokenId}
        >
          {t("settlement.tokenSettlement.getSettlementToken")}
        </Button>
        <Modal
          footer={null}
          title={t("settlement.token.getSettlementToken")}
          open={modalVisible}
          centered
          onCancel={() => setModalVisible(false)}
        >
          {tokenResult ? (
            <Layout className="tw:text-center">
              <CheckCircleFilled
                className={classNames(styles.successIcon, "color--success tw:m-auto")}
                width={"7rem"}
              />
              <Typography.Text className="tw:mb-8! tw:mt-4! tw:text-center" strong>
                {t("Ipg:token.show.successMessage")}
              </Typography.Text>
              <Typography.Paragraph className="tw:my-4!">
                <Trans i18nKey={t("Ipg:token.show.tokenGuideMessage")} components={{ bold: <b /> }} />
                <br />
                <Typography.Text className="text--danger tw:mt-8! tw:block">
                  {t("Ipg:token.show.tokenAttention")}
                </Typography.Text>
              </Typography.Paragraph>
              <Space.Compact style={{ width: "100%" }}>
                <Button
                  className={styles.groupButton}
                  onClick={() => {
                    const copied = copy(tokenResult);
                    if (copied) message.success({ content: t("settlement.token.copied") });
                  }}
                >
                  {t("copy")}
                  <CopyFilled className={"color--cta"} width={"20px"} />
                </Button>
                <Input value={tokenResult} />
              </Space.Compact>
            </Layout>
          ) : (
            <LegacyForm form={form} name={"create"} onFinish={onSubmit}>
              <LegacyForm.Item label={t("paypingPassword")} name="password" rules={[{ required: true }]}>
                <Input.Password />
              </LegacyForm.Item>
              <Button className="tw:w-full" loading={loadingSubmit} type={"primary"} htmlType={"submit"}>
                {t("settlement.token.create")}
              </Button>
            </LegacyForm>
          )}
        </Modal>
      </Space>
    </Space>
  );
};

export default WebServices;
